import { useMemo, useState } from "react";
import { FaSort, FaSortDown, FaSortUp } from "react-icons/fa";
import { useNavigate } from "react-router";
import { useSearchFilter } from "../../../../../utils/functions/useDate";
import {
  useGetHrDepartmentsQuery,
  useGetManagementAppraisalQuery,
} from "../../../../api/hrApi";
import DashboardStyle from "../../../../dashboard/Styles/Dashboard.module.css";
import PageStyle from "../../../Components/Layout/PageLayout";
import { TableActions } from "../../../Components/Misc/Actions";
import Pagination from "../../../Components/Pagination/Pagination";
import {
  CalendarFilter2,
  ProDropFilter,
  SearchFilter,
} from "../../../Components/Search/Search";
import Table from "../../../Components/Table/Table";
import { GetLocalStorage } from "../../../../../utils/functions/GetLocalStorage";

export const getPeriodDateRange = (filterValue) => {
  const today = new Date();
  const startOfYear = new Date(today.getFullYear(), 0, 1);
  const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const lastWeekDate = new Date(today);
  lastWeekDate.setDate(today.getDate() - 7);

  const formatDate = (date) => date.toISOString().split("T")[0];

  switch (filterValue) {
    case 1: // Daily
      return { start: formatDate(today), end: formatDate(today) };
    case 2: // Weekly
      return { start: formatDate(lastWeekDate), end: formatDate(today) };
    case 3: // Monthly
      return { start: formatDate(startOfMonth), end: formatDate(today) };
    case 4: // Quarterly
      const quarterStart = new Date(
        today.getFullYear(),
        Math.floor(today.getMonth() / 3) * 3,
        1
      );
      return { start: formatDate(quarterStart), end: formatDate(today) };
    case 5: // BiAnnual
      const biAnnualStart = new Date(
        today.getFullYear(),
        today.getMonth() >= 6 ? 6 : 0,
        1
      );
      return { start: formatDate(biAnnualStart), end: formatDate(today) };
    case 6: // Yearly
      return { start: formatDate(startOfYear), end: formatDate(today) };
    default:
      return { start: "", end: "" };
  }
};

const HRAppraisal = () => {
  const {
    searchText,
    filter,
    setSearchText,
    setFilter,
    pageSize,
    setPageSize,
    currentPage,
    setCurrentPage,
  } = useSearchFilter({
    initialFilter: 1,
  });
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [sortColumn, setSortColumn] = useState("");
  const [sortDirection, setSortDirection] = useState("asc");
  const [period, setPeriod] = useState(1);
  const [departmentFilter, setDepartmentFilter] = useState("");
  const user = GetLocalStorage();

  const getRoleFromDepartment = (departmentName) => {
    switch (departmentName) {
      case "Human Resources":
        return 3;
      case "ED Operations Office":
      case "ED F&I Office":
        return 4;
      case "MD Office":
        return 5;
      default:
        return null;
    }
  };
  const { data: department } = useGetHrDepartmentsQuery({
    PageSize: 1000,
    PageNumber: 1,
  });
  const reviewerRole = getRoleFromDepartment(user?.departmentName);

  const { start, end } = useMemo(() => getPeriodDateRange(period), [period]);

  const { data, isLoading } = useGetManagementAppraisalQuery({
    PageNumber: currentPage,
    PageSize: pageSize,
    Status: filter,
    SearchTerm: searchText,
    StartDate: startDate,
    EndDate: endDate,
    ObjectiveId: "",
    ReviewerRole: reviewerRole,
    DepartmentId: departmentFilter,
    PeriodStartDate: start,
    PeriodEndDate: end,
  });

  const navigate = useNavigate();

  const flattenedKRAs = useMemo(() => {
    if (!data?.responseObject?.pageItems) return [];

    return data.responseObject.pageItems.map((pageItem) => ({
      ...pageItem,
      appraisalPeriod: pageItem.appraisalPeriodDisplay,
      slaDueDate: "N/A",
    }));
  }, [data]);

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  const sortedData = useMemo(() => {
    if (!flattenedKRAs) return [];
    return [...flattenedKRAs].sort((a, b) => {
      if (!sortColumn) return 0;
      let aValue = a[sortColumn];
      let bValue = b[sortColumn];

      // For strings, make case-insensitive
      if (typeof aValue === "string") {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
      if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
  }, [sortColumn, sortDirection, flattenedKRAs]);

  const renderSortIcon = (column) => {
    if (sortColumn !== column) return <FaSort className="ml-1" />;
    return sortDirection === "asc" ? (
      <FaSortUp className="ml-1" />
    ) : (
      <FaSortDown className="ml-1" />
    );
  };

  const handleView = (id) => {
    if (id) {
      navigate(`${id}/view`);
    }
  };
  const filterBy = [
    {
      name: "Draft",
      filter: 0,
    },
    {
      name: "Awaiting LineManager",
      filter: 1,
    },
    {
      name: "Awaiting Peer Review",
      filter: 2,
    },
    {
      name: "Awaiting Upward Review",
      filter: 3,
    },
    {
      name: "Awaiting Cross Review",
      filter: 4,
    },
    {
      name: "Awaiting HOD",
      filter: 5,
    },
    {
      name: "Awaiting HR",
      filter: 6,
    },
    {
      name: "Completed",
      filter: 7,
    },
    {
      name: "Awaiting ED",
      filter: 8,
    },
    {
      name: "Awaiting MD",
      filter: 9,
    },
  ];
  const periodFilter = [
    { name: "Daily", filter: 1 },
    { name: "Weekly", filter: 2 },
    { name: "Monthly", filter: 3 },
    { name: "QUarterly", filter: 4 },
    { name: "BiAnnual", filter: 5 },
    { name: "Yearly", filter: 6 },
  ];
  const formattedDepartments = department?.responseObject?.pageItems?.map(
    (dept) => ({
      name: dept.name,
      filter: dept.id,
    })
  );

  return (
    <PageStyle demoTitle={"APPRASIAL REVIEW (HR)"} hasTopPageDemo>
      <form className={DashboardStyle.dashboard_filter}>
        <SearchFilter
          value={searchText}
          text={(searchText) => setSearchText(searchText)}
        />
        <ProDropFilter
          filter={filter}
          setFilter={setFilter}
          name={"Appraisal Workflow Status"}
          filterBy={filterBy}
        />
        <CalendarFilter2
          name="Select Date Range"
          setStartDate={setStartDate}
          setEndDate={setEndDate}
          normalDate
        />
        <ProDropFilter
          filter={period}
          setFilter={(value) => setPeriod(Number(value))}
          name={"Period"}
          filterBy={periodFilter}
        />
        <ProDropFilter
          filter={departmentFilter}
          setFilter={setDepartmentFilter}
          name={"Department"}
          filterBy={formattedDepartments}
        />
      </form>
      <div className={DashboardStyle.dashboard_table_holder}>
        <Table>
          <thead>
            <tr>
              <th>S/N</th>
              <th
                onClick={() => handleSort("appraiseeName")}
                style={{ cursor: "pointer" }}
              >
                Appraisee Name {renderSortIcon("appraiseeName")}
              </th>
              <th onClick={() => handleSort("department")}>
                Department {renderSortIcon("department")}
              </th>
              <th onClick={() => handleSort("jobTitle")}>
                Job Title {renderSortIcon("jobTitle")}
              </th>
              <th onClick={() => handleSort("appraisalPeriod")}>
                Appaisal Period {renderSortIcon("appraisalPeriod")}
              </th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td colSpan={10} className="text-center">
                  Loading...
                </td>
              </tr>
            )}
            {sortedData?.length > 0 &&
              sortedData?.map((kra, index) => (
                <tr key={kra.referenceId || index}>
                  <td>{index + 1}</td>
                  <td>{kra?.appraiseeName}</td>
                  <td>{kra?.department}</td>
                  <td>{kra?.jobTitle}</td>
                  <td>{kra?.appraisalPeriod}</td>
                  <td>
                    <TableActions
                      hasChildren={true}
                      url={`${kra.referenceId}/view`}
                    >
                      {[
                        {
                          name: "View Details",
                          action: () => handleView(kra.referenceId),
                        },
                      ]}
                    </TableActions>
                  </td>
                </tr>
              ))}
            {sortedData?.length === 0 && !isLoading && (
              <tr>
                <td colSpan={10} className="text-center">
                  No KRA Records Found
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>
      <Pagination
        last_page={data?.responseObject?.previousPage}
        present_page={data?.responseObject?.currentPage}
        totalRows={data?.responseObject?.totalCount}
        pageSize={data?.responseObject?.pageSize}
        setPageSize={(page) => setPageSize(page)}
        click={(page) => setCurrentPage(page)}
      />
    </PageStyle>
  );
};

export default HRAppraisal;
